import React,{useEffect,useRef,useState} from 'react';
import {AppState} from 'react-native';
import {View,Text,TextInput,TouchableOpacity,StyleSheet} from './rn';
import ProfileSection from './ProfileSection';
import {InfoButton,InfoText,useInfo} from './InfoHint';
import {useTheme} from '../theme/ThemeContext';
import {useTranslation} from '../i18n/I18nContext';
import {api} from '../services/api';
import {radius} from '../theme';
import {profileFieldStyle,profilePalette} from '../theme/profileSection';
import LiveLocationPanel from './LiveLocationPanel';

export type VisitRecord={enabled:boolean;place:string|null;timing:string|null;revision:number;
  placeKind?:'live'|'text';locationShareId?:number|null;liveAvailable?:boolean;liveExpiresAt?:string|null};
export type VisitSnapshot={mine:VisitRecord;incoming:VisitRecord|null;liveOption?:{id:number;expiresAt:string|null}|null};
type Props={token:string;contactId:number;myId:number;onOpenSharing?:()=>void};

export default function VisitInvitation(props:Props) {
  // Parent remounts on identity/contact changes; no account's private wording
  // can flash in another account, even before the next effect is installed.
  return <VisitInvitationSession key={`${props.token}:${props.myId}:${props.contactId}`} {...props}/>;
}
function VisitInvitationSession({token,contactId,onOpenSharing}:Props) {
  const {t,lang}=useTranslation();const colors=useTheme();
  const palette=profilePalette(colors);
  const info=useInfo();
  const [snapshot,setSnapshot]=useState<VisitSnapshot|null>(null);
  const [error,setError]=useState(false),[busy,setBusy]=useState(false);
  const [editing,setEditing]=useState(false),[place,setPlace]=useState(''),[timing,setTiming]=useState('');
  const [placeKind,setPlaceKind]=useState<'text'|'live'>('text'),[shareId,setShareId]=useState<number|null>(null);
  const [viewerShareId,setViewerShareId]=useState<number|null>(null);
  const [revision,setRevision]=useState(0),[refresh,setRefresh]=useState(0);
  const generation=useRef(0),foreground=useRef(AppState.currentState==='active');
  const mounted=useRef(true),saving=useRef(false);
  useEffect(()=>()=>{mounted.current=false;generation.current++;},[]);
  useEffect(()=>{
    const epochRef=generation;
    let timer:ReturnType<typeof setTimeout>|undefined;
    const load=async()=>{
      const epoch=++generation.current;
      if(!foreground.current || saving.current)return;
      try {
        const data=await api.getVisitInvitations(token,contactId);
        if(mounted.current && epoch===generation.current && foreground.current){setSnapshot(data);setError(false);}
      }catch{
        if(mounted.current && epoch===generation.current){setSnapshot(null);setViewerShareId(null);setError(true);}
      }finally{
        if(mounted.current && epoch===generation.current && foreground.current)timer=setTimeout(load,15000);
      }
    };
    load();
    const sub=AppState.addEventListener('change',state=>{
      foreground.current=state==='active';generation.current++;clearTimeout(timer);
      setSnapshot(null);setEditing(false);setViewerShareId(null);
      if(foreground.current)load();
    });
    return()=>{epochRef.current++;clearTimeout(timer);sub.remove();};
  },[token,contactId,refresh]);
  useEffect(()=>{
    if(!snapshot?.incoming?.enabled||snapshot.incoming.placeKind!=='live'||!snapshot.incoming.liveAvailable
      ||snapshot.incoming.locationShareId!==viewerShareId)setViewerShareId(null);
  },[snapshot,viewerShareId]);
  const begin=()=>{
    if(!snapshot)return;
    setPlace(snapshot.mine.place??t('visit.defaultPlace'));setTiming(snapshot.mine.timing??t('visit.defaultTime'));
    setPlaceKind(snapshot.mine.placeKind==='live'?'live':'text');setShareId(snapshot.mine.locationShareId??null);
    setRevision(snapshot.mine.revision);setEditing(true);
  };
  const save=async(enabled:boolean)=>{
    if(!snapshot || saving.current)return;
    if(enabled&&(!timing.trim()||(placeKind==='live'?!liveReady:!place.trim())))return;
    saving.current=true;setBusy(true);generation.current++;
    try {
      await api.saveVisitInvitation(token,contactId,enabled
        ? placeKind==='live'
          ? {enabled:true,revision,placeKind:'live',locationShareId:shareId!,timing:timing.trim()}
          : {enabled:true,revision,place:place.trim(),timing:timing.trim()}
        : {enabled:false,revision:snapshot.mine.revision});
      if(mounted.current){setEditing(false);setSnapshot(null);}
    }catch{
      // An ambiguous response must be reconciled by GET, never retried as PUT.
      if(mounted.current){setEditing(false);setSnapshot(null);setError(true);}
    }finally{
      saving.current=false;
      if(mounted.current){setBusy(false);setRefresh(n=>n+1);}
    }
  };
  const text={color:colors.black,fontSize:16,flexShrink:1};
  const liveOption=snapshot?.liveOption;
  const openAt=(expires:string|null|undefined)=>!expires||Number.isFinite(Date.parse(expires))&&Date.parse(expires)>Date.now();
  const liveReady=!!liveOption&&shareId===liveOption.id&&openAt(liveOption.expiresAt);
  const duration=(expires:string|null|undefined)=>expires&&Number.isFinite(Date.parse(expires))
    ? t('visit.liveUntil',{time:new Date(expires).toLocaleString(lang)}) : t('visit.liveOpenEnded');
  const action=(label:string,id:string,press:()=>void,disabled=false,kind:'primary'|'secondary'|'danger'='secondary')=><TouchableOpacity
    testID={id} accessibilityRole="button" accessibilityState={{disabled}} disabled={disabled}
    onPress={press} style={[styles.action,{backgroundColor:kind==='primary'?palette.selectedSurface:colors.white},
      kind==='secondary'&&{borderColor:colors.gray200},disabled&&styles.disabled]}>
    <Text style={[text,styles.actionText,{color:kind==='primary'?palette.selectedText:kind==='danger'?palette.danger:palette.heading}]}>{label}</Text></TouchableOpacity>;
  const details=(record:VisitRecord,incoming=false)=><View style={styles.details}>
    <View style={styles.detail}><Text style={[styles.label,{color:colors.gray600}]}>{t('visit.place')}</Text>
      <Text style={[text,styles.value]}>{record.placeKind==='live'?t('visit.placeLive'):record.place}</Text>
      {record.placeKind==='live'&&<>
        <Text style={[styles.label,{color:colors.gray600}]}>{record.liveAvailable&&openAt(record.liveExpiresAt)?duration(record.liveExpiresAt):t('visit.liveEnded')}</Text>
        {incoming&&record.liveAvailable&&openAt(record.liveExpiresAt)&&record.locationShareId
          ?action(t('message.liveLocationOpen'),'visit-open-location',()=>setViewerShareId(record.locationShareId!)):null}
      </>}
    </View>
    <View style={styles.detail}><Text style={[styles.label,{color:colors.gray600}]}>{t('visit.time')}</Text>
      <Text style={[text,styles.value]}>{record.timing}</Text></View>
  </View>;
  return <ProfileSection testID="visit-invitation" header={<View style={styles.header}>
    <Text style={[text,styles.title,{color:palette.heading}]}>{t('visit.title')}</Text>
    <InfoButton open={info.open} onPress={info.toggle} subject={t('visit.title')} testID="visit-info"/>
  </View>}>
    <InfoText open={info.open} text={t('visit.hint')} testID="visit-info-text"/>
    {snapshot?.incoming?.enabled && <View testID="visit-incoming" style={[styles.incoming,{borderColor:colors.gray200}]}>
      <Text style={[text,styles.status,{color:palette.accent}]}>{t('visit.incoming')}</Text>
      {details(snapshot.incoming,true)}
    </View>}
    {error && <View style={styles.content}><Text accessibilityLiveRegion="polite" style={[text,{color:colors.gray600}]}>{t('visit.unavailable')}</Text>
      {action(t('common.retry'),'visit-retry',()=>setRefresh(n=>n+1),busy)}</View>}
    {snapshot && !editing && <View style={styles.content}>
      <Text testID="visit-own-state" style={[text,styles.status,{color:snapshot.mine.enabled?palette.accent:colors.gray600}]}>{snapshot.mine.enabled?t('visit.enabled'):t('visit.off')}</Text>
      {snapshot.mine.enabled && details(snapshot.mine)}
      <View style={styles.actions}>{action(t('visit.edit'),'visit-edit',begin,busy,snapshot.mine.enabled?'secondary':'primary')}
        {snapshot.mine.enabled && action(t('visit.revoke'),'visit-revoke',()=>{save(false);},busy,'danger')}</View>
    </View>}
    {editing && snapshot && <View style={styles.editor}>
      <View style={styles.detail}><Text style={[styles.label,{color:colors.gray600}]}>{t('visit.place')}</Text>
      <View style={styles.actions}>
        {(['text','live'] as const).map(kind=><TouchableOpacity key={kind} testID={`visit-place-${kind}`}
          accessibilityRole="radio" accessibilityState={{checked:placeKind===kind,disabled:busy||(kind==='live'&&!liveOption)}}
          disabled={busy||(kind==='live'&&!liveOption)}
          onPress={()=>{setPlaceKind(kind);if(kind==='live')setShareId(liveOption!.id);}}
          style={[styles.option,{borderColor:placeKind===kind?palette.selectedSurface:colors.gray200,
            backgroundColor:placeKind===kind?palette.selectedSurface:colors.white},busy||(kind==='live'&&!liveOption)?styles.disabled:null]}>
          <Text style={[text,{color:placeKind===kind?palette.selectedText:palette.heading}]}>{t(kind==='live'?'visit.placeLive':'visit.placeText')}</Text>
        </TouchableOpacity>)}
      </View>
      {!liveOption&&<>
        <Text style={[styles.label,{color:colors.gray600}]}>{t('visit.liveMissing')}</Text>
        {onOpenSharing&&action(t('visit.openSharing'),'visit-open-sharing',onOpenSharing,busy)}
      </>}
      {placeKind==='text'?
      <TextInput testID="visit-place" accessibilityLabel={t('visit.place')} value={place} onChangeText={setPlace}
        maxLength={160} editable={!busy} multiline style={[text,profileFieldStyle(colors),styles.input]}/>
        :<View testID="visit-live-selection" style={styles.detail}>
          <Text style={[styles.label,{color:colors.gray600}]}>{liveReady?duration(liveOption?.expiresAt):t('visit.liveEnded')}</Text>
          <Text style={[styles.label,{color:colors.gray600}]}>{t('visit.liveSharingHint')}</Text>
        </View>}
      </View>
      <View style={styles.detail}><Text style={[styles.label,{color:colors.gray600}]}>{t('visit.time')}</Text>
      <TextInput testID="visit-time" accessibilityLabel={t('visit.time')} value={timing} onChangeText={setTiming}
        maxLength={160} editable={!busy} multiline style={[text,profileFieldStyle(colors),styles.input]}/></View>
      <View style={styles.actions}>{action(t('visit.save'),'visit-save',()=>{save(true);},busy||(placeKind==='live'?!liveReady:!place.trim())||!timing.trim(),'primary')}
        {action(t('common.cancel'),'visit-cancel',()=>setEditing(false),busy)}</View>
    </View>}
    {viewerShareId!==null&&<LiveLocationPanel key={viewerShareId} visible shareId={viewerShareId}
      fetchPosition={async id=>{try{return await api.visitInvitationLocation(token,contactId,id);}catch(e){if(mounted.current)setViewerShareId(null);throw e;}}}
      onClose={()=>setViewerShareId(null)}/>}
  </ProfileSection>;
}
const styles=StyleSheet.create({
  header:{flexDirection:'row',alignItems:'center',minHeight:44},title:{fontWeight:'600'},
  content:{gap:16,padding:6},status:{fontWeight:'500',lineHeight:23},
  details:{gap:12},detail:{gap:6,minWidth:0},label:{fontSize:13,lineHeight:19},value:{lineHeight:24},
  actions:{gap:8},
  option:{minHeight:48,padding:12,borderWidth:1,borderRadius:radius.control,justifyContent:'center'},
  action:{minHeight:48,minWidth:0,borderWidth:1,borderColor:'transparent',borderRadius:radius.control,
    paddingHorizontal:14,paddingVertical:12,alignItems:'center',justifyContent:'center'},
  actionText:{fontWeight:'500',textAlign:'center',lineHeight:23},disabled:{opacity:0.5},
  incoming:{gap:14,padding:6,paddingBottom:20,marginBottom:14,borderBottomWidth:StyleSheet.hairlineWidth},
  editor:{gap:16,padding:6},input:{textAlignVertical:'top',lineHeight:24},
});
